import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, finalize, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { DailyExpenseRequest } from '@/models/expense-details/daily-expense-request';
import { ExpenseDetails } from '@/models/expense-details/expense-details';

@Component({
  selector: 'app-daily-expense-entry',
  templateUrl: './daily-expense-entry.component.html',
  styleUrl: './daily-expense-entry.component.scss'
})
export class DailyExpenseEntryComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  expenseList: ExpenseDetails[] = [];

  dailyExpenseId = 0;
  isEditMode = false;
  saving = false;
  loadingExpense = false;
  deletingBill = false;

  billPreview: string | null = null;
  billBase64 = '';
  fileType = '';
  selectedFileName = '';

  existingBill = false;
  newBillSelected = false;

  readonly maxFileBytes = 2 * 1024 * 1024;
  readonly today = this.formatDateInput(new Date());

  expenseForm = this.fb.group({
    expenseSeq: [null as number | null, Validators.required],
    expenseDate: [
      this.formatDateInput(new Date()),
      Validators.required
    ],
    amount: [
      null as number | null,
      [
        Validators.required,
        Validators.min(0.01),
        Validators.max(999999999)
      ]
    ],
    description: ['', Validators.required]
  });

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadExpenseMasters();

    const storedId = localStorage.getItem('dailyExpenseId');

    if (storedId) {
      const id = Number(storedId);

      if (Number.isInteger(id) && id > 0) {
        this.dailyExpenseId = id;
        this.isEditMode = true;
        this.fetchExpenseDetails(id);
      } else {
        localStorage.removeItem('dailyExpenseId');
      }
    }
  }

  get f() {
    return this.expenseForm.controls;
  }

  // -----------------------------------------------
  // Load active expense names
  // -----------------------------------------------

  private loadExpenseMasters(): void {
    this.appService.getAllExpenseDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: ExpenseDetails[]) => {
          this.expenseList = (items || [])
            .filter(
              item => (item.status || '').toUpperCase() === 'ACTIVE'
            )
            .sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error: any) => {
          console.error('Expense master error:', error);
          this.toastr.error('Unable to load expense names.');
        }
      });
  }

  // -----------------------------------------------
  // Fetch expense details directly by ID
  // -----------------------------------------------

  private fetchExpenseDetails(id: number): void {
    this.loadingExpense = true;
    this.spinner.show();

    this.appService.getDailyExpenseById(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingExpense = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response: any) => {
          const expense = response?.data ?? response;

          if (
            !expense ||
            Number(expense.dailyExpenseId) !== id
          ) {
            this.toastr.error('Daily expense details not found.');
            return;
          }

          this.dailyExpenseId = Number(expense.dailyExpenseId);
          this.isEditMode = true;

          this.expenseForm.patchValue({
            expenseSeq: Number(expense.expenseSeq),
            expenseDate: this.toDateInputValue(expense.expenseDate),
            amount: Number(expense.amount),
            description: expense.description || ''
          });

          this.existingBill = !!(
            expense.hasBill ||
            expense.billPath ||
            expense.billImageData
          );

          this.billPreview = null;
          this.billBase64 = '';
          this.fileType = '';
          this.selectedFileName = '';
          this.newBillSelected = false;

          if (this.existingBill) {
            // Use the dedicated get-file endpoint.
            this.loadSavedBill(this.dailyExpenseId);
          }
        },
        error: (error: any) => {
          console.error('Fetch expense error:', error);
          this.toastr.error(
            error?.error?.message ||
            'Unable to load expense details.'
          );
        }
      });
  }

  // -----------------------------------------------
  // Fetch existing bill from get-file endpoint
  // Response example: ["base64-string"]
  // -----------------------------------------------

  private loadSavedBill(id: number): void {
    this.appService.getDailyExpenseFile(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (blob: Blob) => {
          if (!(blob instanceof Blob) || blob.size === 0) {
            this.existingBill = false;
            this.billPreview = null;
            return;
          }

          const bytes = new Uint8Array(
            await blob.slice(0, 8).arrayBuffer()
          );

          const isPng =
            bytes[0] === 0x89 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x4E &&
            bytes[3] === 0x47;

          const isJpg =
            bytes[0] === 0xFF &&
            bytes[1] === 0xD8 &&
            bytes[2] === 0xFF;

          const isPdf =
            bytes[0] === 0x25 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x44 &&
            bytes[3] === 0x46;

          if (isPng) {
            this.fileType = 'png';
          } else if (isJpg) {
            this.fileType = 'jpg';
          } else if (isPdf) {
            this.fileType = 'pdf';
          } else {
            console.error('Unknown file format:', blob.type);
            this.toastr.error('The saved bill format is unsupported.');
            return;
          }

          if (
            this.billPreview &&
            this.billPreview.startsWith('blob:')
          ) {
            URL.revokeObjectURL(this.billPreview);
          }

          const mimeType = this.fileType === 'pdf'
            ? 'application/pdf'
            : this.fileType === 'png'
              ? 'image/png'
              : 'image/jpeg';

          const fileBlob = new Blob([blob], { type: mimeType });

          this.billPreview = URL.createObjectURL(fileBlob);
          this.existingBill = true;
          this.newBillSelected = false;
          this.selectedFileName = 'Saved expense bill';
        },

        error: (error: any) => {
          console.error('Get expense bill error:', error);

          this.existingBill = false;
          this.billPreview = null;
        }
      });
  }

  // -----------------------------------------------
  // Select a single bill
  // A new selection replaces the current selection.
  // -----------------------------------------------

  async onBillSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error(
        'Only JPG, JPEG, PNG and PDF files are allowed.'
      );
      input.value = '';
      return;
    }

    try {
      let processedFile: File;

      if (file.type === 'application/pdf') {
        if (file.size > this.maxFileBytes) {
          this.toastr.error('PDF size must be 2 MB or less.');
          input.value = '';
          return;
        }

        processedFile = file;
        this.fileType = 'pdf';
      } else {
        processedFile = await this.compressImage(file, 2);
        this.fileType = 'jpg';
      }

      this.billBase64 = await this.fileToBase64(processedFile);

      this.billPreview = this.fileType === 'pdf'
        ? `data:application/pdf;base64,${this.billBase64}`
        : `data:image/jpeg;base64,${this.billBase64}`;

      this.selectedFileName = file.name;
      this.newBillSelected = true;

      this.toastr.success('Bill selected successfully.');
    } catch (error) {
      console.error('File processing error:', error);
      this.toastr.error(
        'Unable to process the file. Please select another file.'
      );
      input.value = '';
    }
  }

  private fileToBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.split(',')[1] || '');
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private compressImage(
    file: File,
    maxSizeMB: number
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(reader.error);

      reader.onload = () => {
        const image = new Image();

        image.onerror = () => reject(new Error('Invalid image'));

        image.onload = () => {
          const canvas = document.createElement('canvas');
          const maxDimension = 1600;

          const scale = Math.min(
            1,
            maxDimension / Math.max(image.width, image.height)
          );

          canvas.width = Math.round(image.width * scale);
          canvas.height = Math.round(image.height * scale);

          const context = canvas.getContext('2d');

          if (!context) {
            reject(new Error('Canvas is unavailable'));
            return;
          }

          context.drawImage(
            image,
            0,
            0,
            canvas.width,
            canvas.height
          );

          let quality = 0.85;

          const createBlob = (): void => {
            canvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error('Image compression failed'));
                  return;
                }

                if (
                  blob.size <= maxSizeMB * 1024 * 1024 ||
                  quality <= 0.3
                ) {
                  if (blob.size > maxSizeMB * 1024 * 1024) {
                    reject(
                      new Error('Image exceeds 2 MB after compression')
                    );
                    return;
                  }

                  resolve(
                    new File([blob], 'expense-bill.jpg', {
                      type: 'image/jpeg'
                    })
                  );
                  return;
                }

                quality -= 0.1;
                createBlob();
              },
              'image/jpeg',
              quality
            );
          };

          createBlob();
        };

        image.src = String(reader.result);
      };

      reader.readAsDataURL(file);
    });
  }

  // -----------------------------------------------
  // Delete the saved bill from the backend
  // -----------------------------------------------

  deleteSavedBill(): void {
    if (
      this.deletingBill ||
      !this.isEditMode ||
      this.dailyExpenseId <= 0 ||
      !this.existingBill
    ) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this saved bill?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.deletingBill = true;
      this.spinner.show();

      this.appService.deleteDailyExpenseFile(this.dailyExpenseId)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.deletingBill = false;
            this.spinner.hide();
          })
        )
        .subscribe({
          next: () => {
            this.existingBill = false;
            this.newBillSelected = false;
            this.billPreview = null;
            this.billBase64 = '';
            this.fileType = '';
            this.selectedFileName = '';

            Swal.fire({
              title: 'Deleted!',
              text: 'Saved bill deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#198754'
            });
          },
          error: (error: any) => {
            console.error('Delete saved bill error:', error);

            Swal.fire({
              title: 'Error!',
              text: error?.error?.message || 'Unable to delete the saved bill.',
              icon: 'error',
              confirmButtonColor: '#d33'
            });
          }
        });
    });
  }

  // Clear a newly selected file.
  // If an old bill exists, reload it.
  clearSelectedBill(input?: HTMLInputElement): void {
    this.newBillSelected = false;
    this.billPreview = null;
    this.billBase64 = '';
    this.fileType = '';
    this.selectedFileName = '';

    if (input) {
      input.value = '';
    }

    if (this.isEditMode && this.existingBill) {
      this.loadSavedBill(this.dailyExpenseId);
    }
  }

  // -----------------------------------------------
  // Save new expense or update existing expense
  // -----------------------------------------------

  saveExpense(): void {
    if (this.saving || this.loadingExpense) {
      return;
    }

    if (this.expenseForm.invalid) {
      this.expenseForm.markAllAsTouched();
      this.toastr.warning('Please fill in all required fields.');
      return;
    }

    if (this.isEditMode && this.dailyExpenseId <= 0) {
      this.toastr.error('Invalid daily expense ID.');
      return;
    }

    const value = this.expenseForm.getRawValue();
    const date = this.parseDateInput(value.expenseDate || '');

    if (!date || !Number.isFinite(date.getTime())) {
      this.toastr.error('Please select a valid expense date.');
      return;
    }

    const amount = Number(value.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      this.toastr.error('Amount must be greater than zero.');
      return;
    }

    const request: DailyExpenseRequest = {
      expenseSeq: Number(value.expenseSeq),
      dailyExpenseId: this.isEditMode ? this.dailyExpenseId : 0,
      amount,
      expenseDate: date.getTime(),
      description: (value.description || '').trim(),
      billBase64: this.newBillSelected ? this.billBase64 : '',
      fileType: this.newBillSelected ? this.fileType : ''
    };

    this.saving = true;
    this.spinner.show();

    const request$ = this.isEditMode
      ? this.appService.updateDailyExpense(request)
      : this.appService.saveDailyExpense(request);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            this.isEditMode
              ? 'Daily expense updated successfully.'
              : 'Daily expense saved successfully.'
          );

          this.clearEditState();
          this.router.navigate(['/main/daily-expense-view']);
        },
        error: (error: any) => {
          console.error('Save expense error:', error);
          this.toastr.error(
            error?.error?.message || 'Unable to save daily expense.'
          );
        }
      });
  }

  cancel(): void {
    this.clearEditState();
    this.router.navigate(['/main/daily-expense-view']);
  }

  private clearEditState(): void {
    localStorage.removeItem('dailyExpenseId');

    this.dailyExpenseId = 0;
    this.isEditMode = false;
  }

  // -----------------------------------------------
  // Date helpers
  // -----------------------------------------------

  private parseDateInput(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('-').map(Number);

    if (parts.length !== 3) {
      return null;
    }

    return new Date(
      parts[0],
      parts[1] - 1,
      parts[2],
      0,
      0,
      0,
      0
    );
  }

  private toDateInputValue(value: number | string): string {
    let timestamp = Number(value);

    // Support epoch seconds if the API returns seconds.
    if (timestamp > 0 && timestamp < 100000000000) {
      timestamp *= 1000;
    }

    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return this.formatDateInput(new Date());
    }

    return this.formatDateInput(date);
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    localStorage.removeItem('dailyExpenseId');

    this.destroy$.next();
    this.destroy$.complete();
  }
}